import type useEmblaCarousel from 'embla-carousel-react';
import type { UseEmblaCarouselType } from 'embla-carousel-react';

// embla-carousel is not a direct dependency — derive the api and plugin types
// through embla-carousel-react's hook signature instead.
type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
type EmblaPluginType = NonNullable<
    Parameters<typeof useEmblaCarousel>[1]
>[number];

/**
 * Stops the rail dead at its first and last snap, and hands the push that
 * would have stretched it to the page.
 *
 * Embla's `ScrollBounds` lets the track stretch half a viewport past the edge
 * and springs it back, so the rail rubber-bands inside a page that does not.
 * Apple's product rails do the opposite: the row itself never moves past its
 * last slide, and the sideways push runs out into the document, which
 * rubber-bands the whole page about 14px and settles. Measuring their gallery
 * frame by frame, the page header travels the same 14px as the slides do — it
 * is the page bouncing, not the rail.
 *
 * Two halves, because the browser only bounces a page it is told about:
 *
 * `constrain` clamps the target to the limit every tick instead of easing it
 * back, so a drag past the end moves the finger and not the row.
 *
 * The wheel listener gives the edge back to the browser. `WheelGesturesPlugin`
 * calls `preventDefault` on every sideways wheel over the carousel, which is
 * right while the rail still has somewhere to go and wrong once it doesn't —
 * the page can't overscroll on an event that was consumed. Capturing on an
 * ancestor runs before the viewport's own listeners, so stopping the event
 * there means the wheel plugin never sees it and the browser handles it as it
 * would anywhere else on the page.
 */
export function HardEdge(): EmblaPluginType {
    let stopListening = () => {};

    function init(embla: EmblaCarouselType): void {
        const engine = embla.internalEngine();
        const { limit, target, scrollBounds } = engine;

        // A looping rail has no edge to hold, and the engine never calls
        // `constrain` on one.
        if (engine.options.loop) return;

        scrollBounds.constrain = () => {
            target.set(limit.constrain(target.get()));
        };

        const root = embla.rootNode();
        // An ancestor, so the capture phase reaches here before the viewport's
        // own wheel listeners get their turn at the target.
        const scope = root.parentElement ?? root;
        const onWheel = (event: WheelEvent) => {
            // A mostly-vertical wheel is the reader scrolling the page past the
            // carousel, which is already the browser's to handle.
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
            const pushingPastTheEnd =
                event.deltaX < 0
                    ? !embla.canScrollPrev()
                    : !embla.canScrollNext();
            if (pushingPastTheEnd) {
                event.stopImmediatePropagation();
            }
        };

        scope.addEventListener('wheel', onWheel, {
            capture: true,
            passive: true,
        });
        stopListening = () => {
            scope.removeEventListener('wheel', onWheel, { capture: true });
        };
    }

    function destroy(): void {
        // The patched engine is discarded with the embla instance. The
        // listener is not.
        stopListening();
        stopListening = () => {};
    }

    return { name: 'hardEdge', options: {}, init, destroy };
}
